import lscache from "lscache";

const apiPath = "/~/vimeo/";

document.addEventListener("alpine:init", init, { once: true });

function init() {
    window.Alpine.data(
        "vimeoAlbums",
        function (userId, albums = [], sortBy = null, perPage = 6, persistTime = 1, clearCache = false) {
            if (!sortBy) {
                sortBy = "default";
            }
            const storageKey = `vimeo-${perPage}-${sortBy}-${userId}`;

            if (clearCache) {
                lscache.remove(storageKey);
            }

            // Create a storage object that uses lscache for persistence
            const vimeoStorage = {
                getItem() {
                    return lscache.get(storageKey);
                },
                setItem(key, value) {
                    lscache.set(storageKey, value, persistTime);
                },
            };

            return {
                data: this.$persist([]).using(vimeoStorage),
                loading: true,
                firstFetch: true,
                error: "",
                showVideos: false,
                currentAlbum: {},

                async callVimeoAPI(currentAlbum = null) {
                    const path = currentAlbum ? `/${sortBy}/${currentAlbum.page}/${perPage}/${currentAlbum.id}` : "";

                    try {
                        const response = await fetch(apiPath + userId + path);
                        if (response && response.ok) {
                            return await response.json();
                        }
                        return {
                            failed: true,
                            error: `Failed to fetch from Vimeo API: ${response.status} ${response.statusText}`,
                        };
                    } catch (error) {
                        return {
                            failed: true,
                            error,
                        };
                    }
                },

                button: (album) => ({
                    ":tabindex"() {
                        return album.id === this.currentAlbum.id ? 0 : -1;
                    },
                    ":class"() {
                        return album.id === this.currentAlbum.id && "tabs__marker";
                    },
                    "@click"() {
                        this.currentAlbum = album;
                    },
                    "x-text"() {
                        return album.name;
                    },
                }),

                tabs: {
                    "@keydown.home.prevent.stop"() {
                        this.firstAlbum();
                    },
                    "@keydown.page-up.prevent.stop"() {
                        this.firstAlbum();
                    },
                    "@keydown.end.prevent.stop"() {
                        this.lastAlbum();
                    },
                    "@keydown.page-down.prevent.stop"() {
                        this.lastAlbum();
                    },
                    "@keydown.down.prevent.stop"() {
                        this.nextAlbum();
                    },
                    "@keydown.right.prevent.stop"() {
                        this.nextAlbum();
                    },
                    "@keydown.up.prevent.stop"() {
                        this.previousAlbum();
                    },
                    "@keydown.left.prevent.stop"() {
                        this.previousAlbum();
                    },
                },

                selectAlbum() {
                    this.$nextTick(() => {
                        this.$focus.focused().click();
                    });
                },

                firstAlbum() {
                    this.$focus.first();
                    this.selectAlbum();
                },

                lastAlbum() {
                    this.$focus.last();
                    this.selectAlbum();
                },

                nextAlbum() {
                    this.$focus.withWrapAround().next();
                    this.selectAlbum();
                },

                previousAlbum() {
                    this.$focus.withWrapAround().prev();
                    this.selectAlbum();
                },

                // Convenience method to get album by ID from current data
                getAlbumById(id) {
                    return this.data.find((album) => album.id === id) || null;
                },

                // Update album information
                updateAlbum(album) {
                    this.data = this.data.map((item) => (item.id === album.id ? { ...item, ...album } : item));
                },

                init() {
                    this.$watch("currentAlbum", (value, oldValue) => {
                        if (value?.id !== oldValue?.id) {
                            this.fetchAlbum();
                        }
                    });

                    // There are already albums loaded from the persistent storage, so select the first one
                    if (this.data.length) {
                        this.currentAlbum = this.data[0];
                        return;
                    }

                    // No albums loaded yet, fetch them from the API
                    this.fetchAlbums();
                },

                tryAgain() {
                    // If an error screen is shown, wait for a while before trying again
                    this.loading = true;
                    setTimeout(() => {
                        if (this.error === "videos") {
                            this.fetchVideos();
                            return;
                        }
                        this.fetchAlbums();
                    }, 6000);
                },

                async fetchAlbums() {
                    let receivedData = [];
                    try {
                        receivedData = await this.callVimeoAPI();
                        if (receivedData?.error) {
                            throw new Error(receivedData.error);
                        }
                    } catch (error) {
                        console.error("Error on loading the albums:", error);
                        this.error = "fetch";
                        this.stopLoader(0);
                        return;
                    }

                    if (!receivedData.length) {
                        this.error = "albums";
                        this.stopLoader(0);
                        return;
                    }

                    this.error = "";

                    const albumsFromVimeo = {};
                    receivedData.forEach(({ value, label }) => {
                        albumsFromVimeo[value] = label;
                    });

                    // Store the albums into the data
                    const data = [];
                    albums.forEach((id) => {
                        if (albumsFromVimeo[id]) {
                            const currentData = this.getAlbumById(id) || {};
                            data.push({
                                id,
                                name: albumsFromVimeo[id],
                                videos: [],
                                totalPages: null,
                                page: 1,
                                done: false,
                                ...currentData,
                            });
                        }
                    });

                    this.data = data;

                    // Select the first album
                    this.currentAlbum = data[0];
                },

                fetchAlbum() {
                    this.showVideos = false;

                    // If the album is not loaded yet, fetch it
                    if (!this.currentAlbum?.videos?.length) {
                        this.firstFetch = true;
                        this.fetchVideos();
                        return;
                    }

                    // Set loading to true, because we don't want to load more videos if the user make a fast switching between albums
                    this.loading = true;
                    this.stopLoader();
                },

                stopLoader(timeout = 500) {
                    this.showVideos = true;

                    // Stop the loading indicator after a delay
                    setTimeout(() => {
                        this.loading = false;
                    }, timeout);

                    // Reset the firstFetch flag after a short delay
                    if (this.firstFetch) {
                        setTimeout(() => {
                            this.firstFetch = false;
                        }, timeout + 500);
                    }
                },

                async fetchVideos() {
                    // We store the current album in a extra variable, to prevent any issues with reactivity
                    // If the user switches albums quickly, we want to make sure we're storing into the right one
                    const currentAlbum = this.currentAlbum;

                    // If a loading indicator is already shown, and there are already videos loaded and no error occurred, return
                    // Also return if the album is already done loading
                    if ((this.loading && currentAlbum.videos.length && !this.error) || currentAlbum.done) {
                        return;
                    }

                    this.loading = true;
                    let response = null;
                    try {
                        response = await this.callVimeoAPI(currentAlbum);
                        if (!response || response?.error) {
                            throw new Error(response?.error || "Failed to fetch videos");
                        }
                    } catch (error) {
                        console.error("Error on loading the videos:", error);
                        this.error = "videos";
                        this.stopLoader(0);
                        return;
                    }

                    if (!currentAlbum.totalPages) {
                        currentAlbum.totalPages = Math.ceil(response.total / perPage);
                    }

                    this.error = "";

                    const newVideos = response.data.filter(Boolean);

                    // Put the new videos into the data object
                    currentAlbum.videos = [...currentAlbum.videos, ...newVideos];

                    // Increment the page number
                    currentAlbum.page++;

                    // Check if the album is already done loading
                    currentAlbum.done = !!(currentAlbum.page > currentAlbum.totalPages);

                    // Update the data object for the current album
                    this.updateAlbum(currentAlbum);
                    this.currentAlbum = currentAlbum;

                    // Stop the loading indicator
                    this.stopLoader();
                },
            };
        },
    );
}
